import { useRef, useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useKeyboardShortcuts } from '../../../hooks/useKeyboardShortcuts.ts';
import { cn } from '../../../lib/utils.ts';
import type { DocumentActionBlocker } from '../utils/documentActions.ts';
import { BLOCKER_LABELS } from './DocumentActionsMenu.tsx';
import { DeleteDocumentPopup } from './DeleteDocumentPopup.tsx';

type DeleteDocumentButtonProps = {
    blocker: DocumentActionBlocker | null;
    unmatchesFirst: boolean;
    onDelete: () => Promise<void>;
};

export function DeleteDocumentButton({ blocker, unmatchesFirst, onDelete }: DeleteDocumentButtonProps) {
    const [isConfirming, setIsConfirming] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const label = unmatchesFirst ? 'Ontkoppelen en verwijderen' : 'Verwijderen';

    useKeyboardShortcuts({
        shortcuts: [{ keys: ['Delete', 'Backspace'], handler: () => setIsConfirming(true) }],
        enabled: blocker === null && !isConfirming && !isDeleting,
        ownerRef: buttonRef,
    });

    const confirm = async () => {
        setIsDeleting(true);
        try {
            await onDelete();
        } catch {
            // The drawer service already showed the failure to the user.
        } finally {
            setIsDeleting(false);
            setIsConfirming(false);
        }
    };

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setIsConfirming(true)}
                disabled={blocker !== null}
                title={blocker ? BLOCKER_LABELS[blocker] : 'Sneltoets Delete of Backspace'}
                aria-keyshortcuts="Delete Backspace"
                className={cn(
                    'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-[13px] font-semibold text-gray-700 shadow-sm transition-colors',
                    'hover:border-red-300 hover:bg-red-50 hover:text-red-600',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2',
                    'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-300 disabled:hover:bg-white disabled:hover:text-gray-700',
                )}
            >
                <TrashIcon className="h-4 w-4" aria-hidden="true" />
                <span>{label}</span>
            </button>
            <DeleteDocumentPopup
                open={isConfirming}
                isDeleting={isDeleting}
                unmatchesFirst={unmatchesFirst}
                onClose={() => setIsConfirming(false)}
                onConfirm={confirm}
            />
        </>
    );
}
